import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";

interface SectionRevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  direction?: "up" | "left" | "right";
}

const hidden = {
  up: { opacity: 0, y: 50, scale: 0.97, filter: "blur(6px)" },
  left: { opacity: 0, x: -50, scale: 0.97, filter: "blur(6px)" },
  right: { opacity: 0, x: 50, scale: 0.97, filter: "blur(6px)" },
} as const;

const visible = {
  opacity: 1,
  y: 0,
  x: 0,
  scale: 1,
  filter: "blur(0px)",
};

const SectionReveal = ({ children, className = "", delay = 0, direction = "up" }: SectionRevealProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "100px" });
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Small delay so above-the-fold elements animate visibly after paint
    const timer = setTimeout(() => setMounted(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const shouldAnimate = mounted && inView;

  return (
    <motion.div
      ref={ref}
      initial={hidden[direction]}
      animate={shouldAnimate ? visible : hidden[direction]}
      transition={{
        duration: 0.7,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
        filter: { duration: 0.5, delay },
        scale: { duration: 0.5, delay },
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default SectionReveal;
